/*
 * Companion script for the product-overview A/B gallery.
 *
 * ab-test.js already handles variant assignment, showing/hiding, the impression
 * for the primary test, and winner polling. This script covers the three things
 * it cannot:
 *
 *   1. Repairs Swiper on the variant that was hidden at init time. output.js is
 *      deferred in <head> and runs BEFORE ab-test.js, so it initialises Swiper
 *      on a display:none element, measuring every slide at zero width.
 *   2. Tracks Add to Cart as the primary test's click. The buy form lives in a
 *      different grid column, outside the variant wrapper, so ab-test.js's
 *      built-in binding never sees it.
 *   3. Tracks gallery interaction as a separate companion test, so engagement
 *      and conversion are scored independently rather than conflated.
 */
(function () {
  var root = document.querySelector('.product-overview-gallery-ab');
  if (!root) return;

  var testId = root.dataset.abTestId;
  var testName = root.dataset.abTestName || null;
  var threshold = parseInt(root.dataset.abThreshold, 10) || 5;
  var endpoint = root.dataset.abEndpoint || 'https://habitat-app-ab.vercel.app';
  if (!testId) return;

  var ENGAGEMENT_ID = testId + '-engagement';

  // Mirror ab-test.js's assignment so both tests score the same visitor the
  // same way. A declared winner takes precedence, exactly as it does there.
  function currentVariant() {
    return (
      localStorage.getItem('ab_winner_' + testId) || localStorage.getItem('ab_variant_' + testId) || null
    );
  }

  function track(id, name, event) {
    var variant = currentVariant();
    if (!variant) return;
    fetch(endpoint + '/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        testId: id,
        name: name,
        variant: variant,
        event: event,
        threshold: threshold,
        shopDomain: typeof Shopify !== 'undefined' && Shopify.shop ? Shopify.shop : null,
      }),
      keepalive: true,
      credentials: 'omit',
    }).catch(function () {});
  }

  // --- 1. Repair Swiper on whichever variant was hidden at init -------------
  function refreshVisibleSwiper() {
    root.querySelectorAll('[data-ab-variant]').forEach(function (wrapper) {
      if (wrapper.offsetParent === null) return; // still hidden
      var el = wrapper.querySelector('.product-overview-media-carousel.swiper');
      if (el && el.swiper) el.swiper.update();
    });
  }

  // Re-run whenever ab-test.js toggles a variant's display, which happens once
  // on assignment and again if the winner poll swaps the visitor over.
  new MutationObserver(refreshVisibleSwiper).observe(root, {
    attributes: true,
    attributeFilter: ['style'],
    subtree: true,
  });

  // --- 2. Add to Cart = the primary test's click ---------------------------
  // Delegated at document level. A PDP carries several `.add-to-cart-form`
  // elements -- the main overview form, the sticky bar's own form, and the cart
  // drawer's related-product cards -- so `document.querySelector` picks an
  // arbitrary one.
  //
  // Rather than key off a marker attribute, match the form's hidden `id` input
  // against this product's variant ids, emitted on the wrapper by Liquid. That
  // holds regardless of markup changes, and excludes upsell forms for other
  // products.
  //
  // The theme calls preventDefault inside its own submit handler, which does
  // not stop the event dispatching, so capture-phase listening is safe.
  var variantIds = (root.dataset.abVariantIds || '')
    .split(',')
    .map(function (s) { return s.trim(); })
    .filter(Boolean);

  var addFired = false;

  function isThisProductsForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    if (!form.classList.contains('add-to-cart-form')) return false;
    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return false;
    return variantIds.indexOf(String(idInput.value)) !== -1;
  }

  function bindAddToCart() {
    document.addEventListener(
      'submit',
      function (e) {
        if (addFired) return;
        if (!isThisProductsForm(e.target)) return;
        addFired = true; // once per visitor, so CTR reads as conversion rate
        track(testId, testName, 'click');
      },
      true
    );
  }

  // --- 3. Gallery interaction = companion test -----------------------------
  var engagementName = testName ? testName + ' (gallery engagement)' : null;
  var engagementFired = false;

  function bindEngagement() {
    // One impression per visitor for the companion test, mirroring ab-test.js's
    // first-visit rule so its CTR has a comparable denominator.
    var key = 'ab_seen_' + ENGAGEMENT_ID;
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, '1');
      track(ENGAGEMENT_ID, engagementName, 'impression');
    }

    root.addEventListener(
      'click',
      function (e) {
        if (engagementFired) return;
        if (!e.target.closest('.swiper-buttons, .swiper-slide, .hab-swiper-pagination')) return;
        engagementFired = true; // count a visitor as engaged at most once
        track(ENGAGEMENT_ID, engagementName, 'click');
      },
      { passive: true }
    );
  }

  // ab-test.js is deferred, so it has assigned a variant by DOMContentLoaded.
  function start() {
    refreshVisibleSwiper();
    bindAddToCart();
    bindEngagement();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
